import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _typeof from "@babel/runtime/helpers/typeof";
import _awaitAsyncGenerator from "@babel/runtime/helpers/awaitAsyncGenerator";
import _wrapAsyncGenerator from "@babel/runtime/helpers/wrapAsyncGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import partition from 'lodash/partition';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { getXProductHeader } from '../../../provider/start-streaming-response';
import { MAX_PARTS, PART_MAX_SIZE, QUEUE_MAX_ADDITIONAL_ATTEMPTS, TOTAL_PARTS_MAX_SIZE } from '../constants';

/**
 * API Spec: https://hello.atlassian.net/wiki/spaces/AM3/pages/4173571632/Proactive+AI+Trigger+API+Design
 */

function isValidResponse(response) {
  return _typeof(response) === 'object' && response !== null && 'ai_feature_output' in response;
}
function isErrorResponse(response) {
  return _typeof(response) === 'object' && response !== null && 'errorMessage' in response && 'errors' in response && 'statusCode' in response;
}
var serializer = new JSONTransformer();
var sanitizeUrl = function sanitizeUrl(url) {
  return url.replace(/[^\w\-/.:%?=]+/g, '');
};
var getParagraphChunkWithADF = function getParagraphChunkWithADF(doc, editorView, chunk) {
  var from = chunk.from,
    to = chunk.to;
  var content = doc.resolve(from).node();
  if (!content) {
    return;
  }
  var before = doc.resolve(from - 1).nodeBefore;
  var after = doc.resolve(to + 1).nodeAfter;
  return _objectSpread(_objectSpread({}, chunk), {}, {
    content: serializer.encodeNode(content),
    before: before && before.textContent.trim().length !== 0 ? serializer.encodeNode(before) : undefined,
    after: after && after.textContent.trim().length !== 0 ? serializer.encodeNode(after) : undefined
  });
};
function parseResponse(_x2) {
  return _parseResponse.apply(this, arguments);
}
function _parseResponse() {
  _parseResponse = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(response) {
    var body;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          _context2.prev = 0;
          _context2.next = 3;
          return response.json();
        case 3:
          body = _context2.sent;
          if (!isValidResponse(body)) {
            _context2.next = 6;
            break;
          }
          return _context2.abrupt("return", {
            state: 'success',
            body: body
          });
        case 6:
          if (!isErrorResponse(body)) {
            _context2.next = 8;
            break;
          }
          return _context2.abrupt("return", {
            state: 'failed',
            reason: 'backend',
            errors: [body.errorMessage].concat(_toConsumableArray(body.errors)),
            statusCode: body.statusCode
          });
        case 8:
          _context2.next = 13;
          break;
        case 10:
          _context2.prev = 10;
          _context2.t0 = _context2["catch"](0);
          return _context2.abrupt("return", {
            state: 'failed',
            reason: 'parsing',
            errors: ['parsingError'],
            statusCode: response.status
          });
        case 13:
          if (response.body) {
            _context2.next = 15;
            break;
          }
          return _context2.abrupt("return", {
            state: 'failed',
            reason: 'network',
            errors: ['response.body missing'],
            statusCode: response.status
          });
        case 15:
          return _context2.abrupt("return", {
            state: 'failed',
            reason: 'backend',
            errors: ['Invalid response'],
            statusCode: response.status
          });
        case 16:
        case "end":
          return _context2.stop();
      }
    }, _callee2, null, [[0, 10]]);
  }));
  return _parseResponse.apply(this, arguments);
}
export function requestGenerator(_x) {
  return _requestGenerator.apply(this, arguments);
}
function _requestGenerator() {
  _requestGenerator = _wrapAsyncGenerator(function (_ref) {
    var endpoint = _ref.endpoint,
      paragraphs = _ref.paragraphs,
      editorView = _ref.editorView,
      product = _ref.product;
    return /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var doc, abortController, purgedChunkIds, totalParts, attemptCount, _partition, _partition2, withinLimitsParagraphChunks, exceededLimitsParagraphChunks, queue, queueTotalPartsSize, withinLimitsParagraphChunk, paragraphChunkWithADF, queuedChunkIds, payload, startTime, response, result, triggers, errors, recommendations;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (paragraphs.length) {
              _context.next = 4;
              break;
            }
            _context.next = 3;
            return {
              state: 'done'
            };
          case 3:
            return _context.abrupt("return");
          case 4:
            // FIXME: Rather then removing all the work we added in S+G around queing limits and purging requests. I'm going to
            // set the limits to max value, this way the limits are not enforced and the BE just gets all changes always. This
            // way in future if they want to have limits we can just reset the limit settings.
            /**
             * QUEUE CONSTRAINTS:
             * Input size sum (all parts): 10,000 chars
             * Input size per part: 5,000 chars
             * Max parts: 50
             */
            doc = editorView.state.doc;
            abortController = new AbortController();
            purgedChunkIds = [];
            totalParts = paragraphs.length;
            attemptCount = 0; // Partition into parts that are within the part size limit and parts that exceed it
            // This is done due to backend limitations
            _partition = partition(paragraphs, function (p) {
              return p.text.length <= PART_MAX_SIZE;
            }), _partition2 = _slicedToArray(_partition, 2), withinLimitsParagraphChunks = _partition2[0], exceededLimitsParagraphChunks = _partition2[1]; // For all parts that exceed the parts limit, add them to the purged list
            purgedChunkIds.push.apply(purgedChunkIds, _toConsumableArray(exceededLimitsParagraphChunks.map(function (p) {
              return p.id;
            })));
          case 11:
            if (!(withinLimitsParagraphChunks.length && attemptCount <= QUEUE_MAX_ADDITIONAL_ATTEMPTS)) {
              _context.next = 64;
              break;
            }
            attemptCount++;
            queue = [];
            queueTotalPartsSize = 0;
            /**
             * Fill the queue with parts up until the constraints have been met or until all parts have been added
             */
          case 15:
            if (!(withinLimitsParagraphChunks.length && queueTotalPartsSize + withinLimitsParagraphChunks[0].text.length <= TOTAL_PARTS_MAX_SIZE && queue.length <= MAX_PARTS)) {
              _context.next = 24;
              break;
            }
            withinLimitsParagraphChunk = withinLimitsParagraphChunks.shift();
            if (withinLimitsParagraphChunk) {
              _context.next = 19;
              break;
            }
            return _context.abrupt("break", 24);
          case 19:
            queueTotalPartsSize += withinLimitsParagraphChunk.text.length;
            paragraphChunkWithADF = getParagraphChunkWithADF(doc, editorView, withinLimitsParagraphChunk);
            if (paragraphChunkWithADF) {
              queue.push(paragraphChunkWithADF);
            }
            _context.next = 15;
            break;
          case 24:
            if (queue.length) {
              _context.next = 26;
              break;
            }
            return _context.abrupt("break", 64);
          case 26:
            queuedChunkIds = queue.map(function (p) {
              return p.id;
            });
            _context.prev = 27;
            payload = {
              ai_feature_input: {
                // Only add uncached paragraphs to requests, since cached ones have already been yielded
                triggers: queue.map(function (chunk) {
                  var _chunk$before, _chunk$after;
                  return {
                    trigger_id: chunk.id,
                    content_url: sanitizeUrl(document.location.href),
                    trigger_type: 'PARAGRAPH_COMPLETE',
                    paragraph_content: chunk.content,
                    // FIXME: Remove these fallbacks ie. ?? {} the before/after should be optional however the server is treating them as required
                    // once there optional we sould be able to remove them
                    before_context: (_chunk$before = chunk.before) !== null && _chunk$before !== void 0 ? _chunk$before : {},
                    after_context: (_chunk$after = chunk.after) !== null && _chunk$after !== void 0 ? _chunk$after : {}
                  };
                })
              }
            }; // track durations
            startTime = performance.now();
            _context.next = 32;
            return _awaitAsyncGenerator(fetch(endpoint, {
              method: 'POST',
              headers: _objectSpread({
                'Content-Type': 'application/json;charset=UTF-8',
                'X-Experience-Id': 'ai-mate'
              }, getXProductHeader(product)),
              body: JSON.stringify(payload),
              credentials: 'include',
              signal: abortController.signal,
              mode: 'cors'
            }));
          case 32:
            response = _context.sent;
            _context.next = 35;
            return {
              state: 'trackedDuration',
              duration: performance.now() - startTime
            };
          case 35:
            _context.next = 37;
            return _awaitAsyncGenerator(parseResponse(response));
          case 37:
            result = _context.sent;
            if (!(result.state === 'failed')) {
              _context.next = 42;
              break;
            }
            _context.next = 41;
            return _objectSpread(_objectSpread({}, result), {}, {
              failedChunkIds: queuedChunkIds
            });
          case 41:
            return _context.abrupt("break", 64);
          case 42:
            triggers = result.body.ai_feature_output.triggers; // FIXME: This is not ideal, it checks for any errors in the response and then bails.
            // it would be nice to handle suggestions which are also included with the errors.
            errors = triggers.reduce(function (acc, trigger) {
              if ('errors' in trigger) {
                acc.push(trigger.errors.error_type);
              }
              return acc;
            }, []);
            recommendations = triggers.reduce(function (acc, trigger) {
              if ('recommendations' in trigger) {
                return acc.concat(trigger.recommendations.map(function (recommendation) {
                  var adf = recommendation.transformation_details.replacement_adf;
                  var replacement = adf.type !== 'doc' ? {
                    version: 1,
                    type: 'doc',
                    content: [adf]
                  } : adf;
                  return {
                    chunkId: trigger.trigger_id,
                    id: recommendation.recommendation_id,
                    transformContent: serializer.parse(replacement),
                    transformType: recommendation.transformation_type,
                    transformAction: recommendation.recommendation_name
                  };
                }));
              }
              return acc;
            }, []);
            if (!recommendations.length) {
              _context.next = 48;
              break;
            }
            _context.next = 48;
            return {
              state: 'parsed',
              recommendations: recommendations
            };
          case 48:
            if (!errors.length) {
              _context.next = 51;
              break;
            }
            _context.next = 51;
            return {
              state: 'failed',
              reason: 'backend',
              errors: errors,
              statusCode: response.status,
              failedChunkIds: queuedChunkIds
            };
          case 51:
            _context.next = 62;
            break;
          case 53:
            _context.prev = 53;
            _context.t0 = _context["catch"](27);
            if (!(_context.t0.name === 'AbortError')) {
              _context.next = 60;
              break;
            }
            _context.next = 58;
            return {
              state: 'failed',
              reason: 'aborted',
              errors: ['Streaming aborted'],
              statusCode: _context.t0.status,
              failedChunkIds: queuedChunkIds
            };
          case 58:
            _context.next = 62;
            break;
          case 60:
            _context.next = 62;
            return {
              state: 'failed',
              reason: 'unhandled',
              errors: _context.t0 instanceof Error && ['RangeError', 'TypeError', 'TransformError'].includes(_context.t0.name) ? [_context.t0.message] : ['unhandled'],
              statusCode: _context.t0.status,
              failedChunkIds: queuedChunkIds
            };
          case 62:
            _context.next = 11;
            break;
          case 64:
            /**
             * Purge any remaining valid paragraphs
             */
            if (withinLimitsParagraphChunks.length) {
              purgedChunkIds.push.apply(purgedChunkIds, _toConsumableArray(withinLimitsParagraphChunks.map(function (p) {
                return p.id;
              })));
            }
            if (!purgedChunkIds.length) {
              _context.next = 68;
              break;
            }
            _context.next = 68;
            return {
              state: 'purged',
              totalParts: totalParts,
              totalPurgedParts: purgedChunkIds.length,
              purgedChunkIds: purgedChunkIds
            };
          case 68:
            _context.next = 70;
            return {
              state: 'done'
            };
          case 70:
            return _context.abrupt("return");
          case 71:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[27, 53]]);
    })();
  });
  return _requestGenerator.apply(this, arguments);
}